import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Users, ChevronDown, Plus, User, Check } from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { useNavigate } from 'react-router-dom';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FamilySelector = ({ selectedMember, onSelectMember }) => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchMembers();
  }, []);

  const fetchMembers = async () => {
    try {
      const token = localStorage.getItem('sehatSetu_token');
      const response = await axios.get(`${API_URL}/family/members`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMembers(response.data.members || []);
      
      // Set default to self if not selected
      if (!selectedMember && response.data.members?.length > 0) {
        const selfMember = response.data.members.find(m => m.is_self);
        if (selfMember) {
          onSelectMember(selfMember);
        }
      }
    } catch (error) {
      console.error('Error fetching family members:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRelationshipLabel = (relationship) => {
    const labels = {
      self: 'Me',
      grandparent: 'Grandparent',
      parent: 'Parent',
      spouse: 'Spouse',
      child: 'Child',
      sibling: 'Sibling',
      other: 'Other'
    };
    return labels[relationship] || relationship;
  };

  const getInitials = (name) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  if (loading) {
    return (
      <div className="h-10 w-32 bg-slate-100 animate-pulse rounded-lg"></div>
    );
  }

  const currentMember = selectedMember || members.find(m => m.is_self) || members[0];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="outline" 
          className="flex items-center gap-2 h-10 px-3 border-slate-200 hover:bg-slate-50"
          data-testid="family-selector"
        >
          <div 
            className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-medium"
            style={{ backgroundColor: currentMember?.avatar_color || '#0D2340' }}
          >
            {currentMember?.is_self ? (
              <User className="h-3 w-3" />
            ) : (
              getInitials(currentMember?.name || 'U')
            )}
          </div>
          <span className="text-sm font-medium max-w-[100px] truncate">
            {currentMember?.is_self ? 'Me' : currentMember?.name}
          </span>
          <ChevronDown className="h-4 w-4 text-slate-400" />
        </Button>
      </DropdownMenuTrigger>
      
      <DropdownMenuContent align="start" className="w-56">
        <div className="px-2 py-1.5 text-xs font-medium text-slate-500">
          Switch Profile
        </div>
        
        {members.map((member) => (
          <DropdownMenuItem
            key={member.id}
            onClick={() => onSelectMember(member)}
            className="flex items-center gap-3 cursor-pointer"
            data-testid={`family-member-${member.id}`}
          >
            <div 
              className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-medium"
              style={{ backgroundColor: member.avatar_color || '#0D2340' }}
            >
              {member.is_self ? (
                <User className="h-4 w-4" />
              ) : (
                getInitials(member.name)
              )}
            </div>
            <div className="flex-1">
              <div className="text-sm font-medium">
                {member.is_self ? 'Me' : member.name}
              </div>
              <div className="text-xs text-slate-500">
                {getRelationshipLabel(member.relationship)}
              </div>
            </div>
            {currentMember?.id === member.id && (
              <Check className="h-4 w-4 text-green-500" />
            )}
          </DropdownMenuItem>
        ))}
        
        <DropdownMenuSeparator />
        
        <DropdownMenuItem 
          onClick={() => navigate('/family')}
          className="flex items-center gap-3 cursor-pointer text-[#0D2340]"
          data-testid="manage-family-btn"
        >
          <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
            <Plus className="h-4 w-4" />
          </div>
          <div className="text-sm font-medium">Manage Family</div>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default FamilySelector;
